import React, { Fragment, useEffect, useState } from "react";
import { withRouter } from "react-router-dom";
import { CircularProgress } from "@material-ui/core";
import Icon from "../../components/Icon";
// The generic "GET with the admin's session" hook; it lives with the survey page, its first user.
import { useSurveyRequest as useAdminRequest, formatDateTime } from "../SurveyInsight/api";
import { PUSH_RESULTS, SEND_STATUS_LABEL, USER_SCOPE_OPTIONS } from "./api";
import "./style.scss";

const PAGE_SIZE = 20;

// Always shown, even at zero: "how many went through, how many did not" is the question.
const ALWAYS_SHOWN = ["sent", "failed"];

const resultOf = status => PUSH_RESULTS.find(result => result.status === status) || { label: status, key: "" };

/*
 * Delivery log of one sent notification (/Notification_DeliveryLog?id=...): of the customers it
 * went to, how many the push reached, how many it did not, and why - read from be-notification's
 * per-customer results. The inbox row is written for everyone either way; this is only about the
 * banner on the phone. Opened from the status of a row on Notification List.
 */
function DeliveryLog({ history, location }) {
  const notificationId = new URLSearchParams(location.search).get("id");
  const request = useAdminRequest();
  const [oFilter, SETxFilter] = useState("");
  const [oPage, SETxPage] = useState(1);
  const [oLog, SETxLog] = useState(null);
  const [oLoading, SETxLoading] = useState(false);
  const [oError, SETxError] = useState("");

  useEffect(() => {
    if (!notificationId) {
      SETxError("No notification selected");
      return undefined;
    }
    let cancelled = false;
    SETxLoading(true);
    SETxError("");
    request("recipients", {
      id: notificationId,
      query: { pushStatus: oFilter, page: oPage, limit: PAGE_SIZE }
    })
      .then(result => {
        if (!cancelled && result.data) {
          SETxLog(result.data);
        }
      })
      .catch(error => {
        if (!cancelled) {
          SETxError(error.detail || error.message);
        }
      })
      .finally(() => {
        if (!cancelled) {
          SETxLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [notificationId, oFilter, oPage]);

  const notification = (oLog && oLog.notification) || null;
  const summary = (oLog && oLog.summary) || null;
  const pagination = (oLog && oLog.pagination) || { page: 1, totalPages: 0, total: 0 };
  const scope = notification ? USER_SCOPE_OPTIONS.find(option => option.value === notification.userScope) : null;

  const onFilter = status => {
    SETxPage(1);
    SETxFilter(current => (current === status ? "" : status));
  };

  return (
    <Fragment>
      {oLoading ? (
        <div className="funds-list-progress">
          <CircularProgress />
          <p className="funds-list-progress-text">Loading delivery log...</p>
        </div>
      ) : null}

      <div className="noti-list-container">
        <div className="noti-list-topbar">
          <div className="noti-list-titles">
            <h2 className="noti-list-heading">Delivery log{notification ? ` — ${notification.code}` : ""}</h2>
            <p className="noti-list-subheading">
              Who this notification went to, and whether the push reached their phone
            </p>
          </div>
          <div className="noti-list-actions">
            <button type="button" className="delivery-log-back" onClick={() => history.push("/Notification_List")}>
              <Icon name="arrowLeft" size={18} />
              Back
            </button>
          </div>
        </div>

        {oError ? <div className="noti-list-error">{oError}</div> : null}

        {notification ? (
          <div className="delivery-log-head">
            <p className="delivery-log-title">{notification.header}</p>
            <p className="delivery-log-meta">
              {scope ? scope.label : notification.userScope} ·{" "}
              <span className={`noti-list-status-text ${(notification.sendStatus || "").toLowerCase()}`}>
                {SEND_STATUS_LABEL[notification.sendStatus] || notification.sendStatus}
              </span>{" "}
              · {formatDateTime(notification.timeSent)}
            </p>
            {notification.lastError ? <p className="delivery-log-error">{notification.lastError}</p> : null}
          </div>
        ) : null}

        {summary ? (
          <div className="delivery-log-summary">
            <button
              type="button"
              className={`delivery-log-card total${oFilter === "" ? " active" : ""}`}
              onClick={() => onFilter("")}
            >
              <span className="delivery-log-card-value">{summary.total}</span>
              <span className="delivery-log-card-label">Recipients</span>
            </button>
            {PUSH_RESULTS.filter(result => ALWAYS_SHOWN.includes(result.key) || summary[result.key] > 0).map(result => (
              <button
                type="button"
                key={result.status}
                className={`delivery-log-card ${result.key}${oFilter === result.status ? " active" : ""}`}
                onClick={() => onFilter(result.status)}
              >
                <span className="delivery-log-card-value">{summary[result.key]}</span>
                <span className="delivery-log-card-label">{result.label}</span>
              </button>
            ))}
          </div>
        ) : null}

        {oLog ? (
          <Fragment>
            <div className="delivery-log-table-wrap">
              <table className="delivery-log-table">
                <colgroup>
                  <col style={{ width: 320 }} />
                  <col style={{ width: 150 }} />
                  <col />
                  <col style={{ width: 180 }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>Customer uuid</th>
                    <th>Push result</th>
                    <th>Detail</th>
                    <th>Read</th>
                  </tr>
                </thead>
                <tbody>
                  {oLog.data.map(row => {
                    const result = resultOf(row.pushStatus);
                    return (
                      <tr key={row.customerUuid}>
                        <td className="delivery-log-uuid">{row.customerUuid}</td>
                        <td>
                          <span className={`delivery-log-badge ${result.key}`}>{result.label}</span>
                        </td>
                        <td className="delivery-log-detail">{row.pushError || "-"}</td>
                        <td>{row.readAt ? formatDateTime(row.readAt) : "-"}</td>
                      </tr>
                    );
                  })}
                  {oLog.data.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="delivery-log-empty">
                        No recipients
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 ? (
              <div className="noti-list-pager">
                <button type="button" disabled={oPage <= 1 || oLoading} onClick={() => SETxPage(oPage - 1)}>
                  Previous
                </button>
                <span>
                  Page {pagination.page} / {pagination.totalPages} ({pagination.total} total)
                </span>
                <button
                  type="button"
                  disabled={oPage >= pagination.totalPages || oLoading}
                  onClick={() => SETxPage(oPage + 1)}
                >
                  Next
                </button>
              </div>
            ) : null}
          </Fragment>
        ) : null}
      </div>
    </Fragment>
  );
}

export default withRouter(DeliveryLog);
